import React from 'react';
import { graphql } from 'gatsby';
import { css } from '@emotion/react';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import { colors } from '../../primitives/colors';
import { StyledButtonAsLink } from '../../primitives/styled-button';
import Container from '../../primitives/grid/container';
import Image from '../../primitives/image';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import { breakpoints } from '../../primitives/tokens';

const styles = {
    communityBannerWrap: (fullFill: boolean) => css({
        paddingTop: '160px',
        paddingBottom: '208px',
        minHeight: fullFill ? '100vh' : 'unset',
        position: 'relative',
        [breakpoints.tb]: {
            paddingTop: '80px',
            paddingBottom: '80px',
        },
        [breakpoints.mb]: {
            paddingTop: '56px',
        },
    }),
    content: (alignment: string) => css({
        maxWidth: (alignment === 'right' || alignment === 'left') ? '448px' : undefined,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        position: 'relative',
        zIndex: 10,
        padding: '32px',
        color: colors.shadesBlack,
        marginLeft: (alignment === 'right' || alignment === 'center') ? 'auto' : undefined,
        marginRight: (alignment === 'left' || alignment === 'center') ? 'auto' : undefined,
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        borderRadius: '5px',
    }),
    headline: css({
        lineHeight: 1.19,
        fontSize: '36px',
        marginBottom: '24px',
    }),
    copy: css({
        fontSize: '20px',
        lineHeight: 1.375,
    }),
    linkWrap: css({
        marginTop: '20px',
    }),
    icon: css({
        marginBottom: '32px',
        maxHeight: '77px',
    }),
    backgroundImageWrap: css({
        position: 'absolute',
        top: '0',
        right: '0',
        bottom: '0',
        left: '0',
    }),
    backgroundImage: css({
        objectFit: 'cover',
        zIndex: 0,
        width: '100%',
        height: '100%',
    }),
};

const CommunityBanner: React.FC<Queries.CommunityBannerDataFragment> = (data) => {
    const { background, content } = data;

    if (!background || !content) return null;

    const {
        alignment, headline, copy, icon, buttonLink, disableContent,
    } = content;
    const { image, fillScreenHeight } = background;

    return (
        <div css={styles.communityBannerWrap(!!fillScreenHeight)}>
            <Container>
                {!disableContent && (
                    <div css={styles.content(alignment || 'left')}>
                        {!!icon?.url && <Image css={styles.icon} image={icon} alt={icon.title || ''} />}
                        <h3
                            css={styles.headline}
                            /* eslint-disable-next-line react/no-danger */
                            dangerouslySetInnerHTML={{
                                __html: apCaseOnlyTitleTags(headline),
                            }}
                        />
                        <div
                            css={[RichTextEditorStyles, styles.copy]}
                            /* eslint-disable-next-line react/no-danger */
                            dangerouslySetInnerHTML={{ __html: copy || '' }}
                        />
                        {(buttonLink?.title && buttonLink.href) && (
                            <div css={styles.linkWrap}>
                                <StyledButtonAsLink to={buttonLink.href}>{buttonLink.title}</StyledButtonAsLink>
                            </div>
                        )}
                    </div>
                )}
                {!!image?.url && (
                    <div css={styles.backgroundImageWrap}>
                        <Image css={styles.backgroundImage} image={image} alt={image.description || ''} />
                    </div>
                )}
            </Container>
        </div>
    );
};

export const query = graphql`
    fragment CommunityBannerData on CsCommunityBanner {
        ... on CsCommunityBanner {
            background {
                fillScreenHeight
                image {
                    filename
                    title
                    url
                    description
                    contentStackAsset(width: 1440)
                }
            }
            content {
                disableContent
                alignment
                headline
                copy
                buttonLink {
                    title
                    href
                }
                icon {
                    filename
                    title
                    url
                    contentStackAsset(width: 100)
                }
            }
        }
    }
`;

export default CommunityBanner;
